import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import PhotoList from '@z-components/molecules/PhotoList';
import ProProfileClientContent from '@z-components/molecules/ProProfileClientContent';
import ProProfileIntroduction from '@z-components/molecules/ProProfileIntroduction/ProProfileIntroduction';
import ProCaseStudyContent from 'modules/users/modules/profiles/components/ProCaseStudyContent';
import TabNavigation from 'components/molecules/TabNavigation';

import ListCaseStudies from 'modules/users/modules/profiles/components/ListCaseStudies';
import ProServiceContent from 'modules/users/modules/profiles/components/ProServiceContent';
import ProReviewContent from 'modules/users/modules/profiles/components/ProReviewContent';
import { useApi } from 'src/hooks';
import { addTransformToCloudinaryUrl } from 'utils/url';
import { ProProfilePro } from '../types';
import type { BlogPost } from 'modules/marketing-pages/marketing-page.types';

import styles from './ProProfileDetails.module.scss';
import { SLUG_CASE_STUDIES, SLUG_REVIEWS, SLUG_PHOTOS } from 'constants/pro-profile';

import {
  trackingEventForRequestCreation,
  GA4EventRequestCreation,
} from 'utils/eventTrackingForGA4';

import { isMobile } from 'utils/layout';

interface Props {
  featureFlags: { isReviewsQAEnabled: boolean };
  internalBlogPosts: Array<BlogPost>;
  internalBlogCostPosts: Array<BlogPost>;
  jobTypeId?: string;
  setJobTypeTitle: (jobTypeId: string) => void;
  user: ProProfilePro;
}

const TAB_NAMES = {
  PROFILE: 'tab-profile',
  SERVICES: 'tab-services',
  PHOTOS: 'tab-photos',
  REVIEWS: 'tab-reviews',
  CASE_STUDIES: 'tab-case-studies',
};

const RAND_KEY_RADIX = 36;
const RAND_KEY_LENGTH = 7;
const randomKey = () => Math.random().toString(RAND_KEY_RADIX).substring(RAND_KEY_LENGTH);

// photo list
const MOBILE_PHOTOS_DISPLAY_COUNT = 10;
const DESKTOP_PHOTOS_DISPLAY_COUNT = 20;

// component for z-web pro-profile page
const ProProfileDetails: FC<Props> = ({
  featureFlags,
  internalBlogPosts,
  internalBlogCostPosts,
  jobTypeId: currentJobTypeIdParam,
  user,
  setJobTypeTitle,
}) => {
  const {
    availablePrefectures,
    currentTier = { id: 'new-user' },
    jobProfiles = [],
    name,
    privateProfile = {},
    profile = {},
    proProfile,
    reviews = {
      counts: [0, 0, 0, 0, 0],
      list: [],
      total: 0,
    },
    slug,
    lastOnline,
    proHiredCount,
    proCaseStudies = { totalCaseStudies: 0 },
  } = user;

  const {
    i18n: { language },
    t,
  } = useTranslation();
  const router = useRouter();

  const [selectedTabName, setSelectedTabName] = useState(TAB_NAMES.PROFILE);
  const currentJobTypeId = currentJobTypeIdParam === SLUG_CASE_STUDIES ? '' : currentJobTypeIdParam;
  const [selectedVertical, setSelectedVertical] = useState(currentJobTypeId ?? '');
  const [isMobileLayout, setIsMobileLayout] = useState(false);

  useEffect(() => {
    if (currentJobTypeId) {
      setSelectedVertical(currentJobTypeId);
      setSelectedTabName(TAB_NAMES.SERVICES); // select "Services" Tab if currentJobTypeId
    }
  }, [currentJobTypeId]);

  // Case-study: setSelectedTab by currentJobTypeIdParam
  useEffect(() => {
    if (currentJobTypeIdParam === SLUG_CASE_STUDIES && proCaseStudies.totalCaseStudies > 0) {
      setSelectedTabName(TAB_NAMES.CASE_STUDIES);
    }
    if (currentJobTypeIdParam === SLUG_REVIEWS && reviews.list.length > 0) {
      setSelectedTabName(TAB_NAMES.REVIEWS);
    }
    if (
      currentJobTypeIdParam === SLUG_PHOTOS &&
      proProfile.photos &&
      proProfile.photos.length > 0
    ) {
      setSelectedTabName(TAB_NAMES.PHOTOS);
    }
  }, [currentJobTypeIdParam]);

  // trackingEvent by selectedVertical
  useEffect(() => {
    if (selectedVertical) {
      const jobType = jobProfiles.find((item) => item.jobTypeId === selectedVertical);
      const categories = jobType?.categories;

      trackingEventForRequestCreation(GA4EventRequestCreation.proProfileViewed, {
        jobTypeId: selectedVertical,
        // eslint-disable-next-line id-length
        C1: categories?.[0],
        // eslint-disable-next-line id-length
        C2: categories?.[1],
      });
    } else {
      trackingEventForRequestCreation(GA4EventRequestCreation.proProfileViewed);
    }
  }, [selectedVertical, jobProfiles]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMobileLayout(isMobile(window.screen.width));
    }
  }, []);

  const rating = useMemo(() => {
    const totalRatings = reviews.counts.reduce(
      (total, count, index) => total + count * (index + 1),
      0
    );
    const totalCount = reviews.counts.reduce((total, count) => total + count, 0);
    // Calculate the average rating
    const averageRating = totalCount === 0 ? 0 : totalRatings / totalCount;
    return averageRating;
  }, [reviews]);

  const review = {
    count: reviews.list.length,
    rating,
  };

  const avatar = useMemo(
    () =>
      profile.avatar
        ? addTransformToCloudinaryUrl(profile.avatar, { dpr: '1.0', width: 150 })
        : undefined,
    [profile.avatar]
  );

  const { applyId } = router.query;
  const [, markProWebsiteViewed] = useApi(
    {
      method: 'POST',
      url: `/apply/${applyId as string}/view-pro-website`,
    },
    { manual: true }
  );
  const handleProWebsiteUrlClick = useCallback(async () => {
    if (!applyId) {
      return;
    }
    await markProWebsiteViewed();
  }, [applyId, markProWebsiteViewed]);

  const handleGoToReviewClick = useCallback(() => {
    if (review.count) {
      setSelectedTabName(TAB_NAMES.REVIEWS);
    }
  }, [proProfile.photos, review.count]);

  const handleTabClick = useCallback(
    (tabName: string) => {
      setSelectedTabName(tabName);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window && tabName === TAB_NAMES.PROFILE) {
        history.pushState(
          '',
          '',
          `${window.location.origin}${language === 'en' ? '/en' : ''}/profile/${slug}/pro`
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window && tabName === TAB_NAMES.CASE_STUDIES) {
        history.pushState(
          '',
          '',
          `${window.location.origin}${
            language === 'en' ? '/en' : ''
          }/profile/${slug}/pro/case-studies`
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window && tabName === TAB_NAMES.REVIEWS) {
        history.pushState(
          '',
          '',
          `${window.location.origin}${language === 'en' ? '/en' : ''}/profile/${slug}/pro/reviews`
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (window && tabName === TAB_NAMES.PHOTOS) {
        history.pushState(
          '',
          '',
          `${window.location.origin}${language === 'en' ? '/en' : ''}/profile/${slug}/pro/photos`
        );
      }
    },
    [language, slug]
  );

  const getTabsContent = () => {
    const tabsContent = [
      {
        label: t('users:profile.proDescriptionTab'),
        content: (
          <>
            <section id="pro-profile-introduction" className={styles['pro-profile-introduction']}>
              <ProProfileIntroduction
                isProView={false}
                isShowWebsiteURL={false}
                language={language as 'en' | 'ja'}
                profile={proProfile}
              />
            </section>
            <section id="case-study-in-intro" className={styles['case-study']}>
              <ListCaseStudies
                title={{
                  base: t('users:profile.caseStudyBase'),
                  prefix: t('users:profile.caseStudyUserPrefix', { name }),
                }}
                variant={isMobileLayout ? 'swipe' : ''}
              />
            </section>
          </>
        ),
        key: randomKey(),
      },
      {
        label: t('users:profile.serviceTab'),
        content: (
          <ProServiceContent
            featureFlags={featureFlags}
            internalBlogPosts={internalBlogPosts}
            internalBlogCostPosts={internalBlogCostPosts}
            jobTypeId={currentJobTypeId}
            pro={user}
            setJobTypeTitle={setJobTypeTitle}
          />
        ),
        key: randomKey(),
      },
    ];
    const tabsOrder: { [tabName: string]: number } = {};
    tabsOrder[`${TAB_NAMES.PROFILE}`] = Object.keys(tabsOrder).length; // push tab index with its name into the list
    tabsOrder[`${TAB_NAMES.SERVICES}`] = Object.keys(tabsOrder).length;

    if (proProfile.photos?.length) {
      tabsContent.push({
        label: t('users:profile.photosTab', { total: proProfile.photos.length }),
        content: (
          <section id="pro-photos" className={styles['pro-photos']}>
            <PhotoList
              translations={{
                noCaption: '',
                seeMoreLabel: t('common:seeMore'),
              }}
              photos={proProfile.photos}
              rowLimit={isMobileLayout ? MOBILE_PHOTOS_DISPLAY_COUNT : DESKTOP_PHOTOS_DISPLAY_COUNT}
              seeMorePosition="center"
            />
          </section>
        ),
        key: randomKey(),
      });
      tabsOrder[`${TAB_NAMES.PHOTOS}`] = Object.keys(tabsOrder).length;
    }

    if (review.count) {
      tabsContent.push({
        label: t('users:profile.reviewTab', { total: review.count }),
        content: (
          <ProReviewContent
            featureFlags={featureFlags}
            reviews={reviews}
            averageRating={review.rating}
          />
        ),
        key: randomKey(),
      });
      tabsOrder[`${TAB_NAMES.REVIEWS}`] = Object.keys(tabsOrder).length;
    }

    if (proCaseStudies.totalCaseStudies > 0) {
      const profileCaseStudyUrl =
        typeof window === 'undefined'
          ? ''
          : `${window.location.origin}${
              language === 'en' ? '/en' : ''
            }/profile/${slug}/pro/case-studies`;
      tabsContent.push({
        label: t('users:profile.caseStudyTab', { total: proCaseStudies.totalCaseStudies }),
        content: (
          <ProCaseStudyContent base={''} prefix={''} profileCaseStudyUrl={profileCaseStudyUrl} />
        ),
        key: randomKey(),
      });
      tabsOrder[`${TAB_NAMES.CASE_STUDIES}`] = Object.keys(tabsOrder).length;
    }

    return { tabsContent, tabsOrder };
  };

  const { tabsContent, tabsOrder } = getTabsContent();
  return (
    <div className={styles['pro-profile-detail']}>
      <div className={styles['wrapper']} id="alkdsjafhlkjdfhalkdjhal">
        <div id="content" className={styles.content}>
          <ProProfileClientContent
            language={language as 'en' | 'ja'}
            onReviewClick={handleGoToReviewClick}
            onWebsiteUrlClick={handleProWebsiteUrlClick}
            proData={{
              availablePrefectures,
              avatar,
              location: proProfile.location?.title ?? '',
              tier: currentTier.id,
              title: name ?? '',
              verifiedBadges: {
                governmentId: privateProfile.governmentIdVerified ?? false,
                mail: privateProfile.emailVerified ?? false,
                phone: privateProfile.phoneNumberVerified ?? false,
              },
              review,
              lastOnline,
              website: proProfile.website,
              proHiredCount,
              proCaseStudiesCount: proCaseStudies.totalCaseStudies,
            }}
          />
        </div>
      </div>

      <TabNavigation
        activeTabName={selectedTabName}
        list={tabsContent}
        onTabClick={handleTabClick}
        tabsOrder={tabsOrder}
      />
    </div>
  );
};

export default ProProfileDetails;
export type { Props as ProProfileDetailsProps };
